import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ButtonGroup,
  useToast,
  useSteps,
  Stack,
  Text,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  Stepper,
  StepSeparator,
  Icon,
  Badge,
  HStack,
} from '@chakra-ui/react'
import { LuSend } from 'react-icons/lu'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import RequestDetailsStep from './components/RequestDetailsStep'
import DoctorSelectionStep from './components/DoctorSelectionStep'
import ReviewStep from './components/ReviewStep'
import PatientSelectionStep from './components/PatientSelectionStep'
import { useState, useEffect, useRef } from 'react'
import { CheckIcon } from '@chakra-ui/icons'
import { AxiosError } from 'axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetPatient } from '../../../api/api_hooks/patients'
import { useNewSharedDiagnostic } from '../../../api/api_hooks/shared-diagnostic'
import { NewRequest } from '../../../utils/schema'
import Plus from '../../../assets/svg/plus'
import { useTranslation } from 'react-i18next'

export type RequestFormData = {
  patientId: string | number | null
  title: string
  description: string
  category: string
  tagIds: number[]
  files: File[]
  receiverEmail: string
  openWhiteBoard: boolean
}

export default function NewRequestModal({
  onRequestCreated,
  contextPatientId,
}: {
  onRequestCreated?: () => void
  contextPatientId?: string | number | null
}) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { data: patientData } = useGetPatient()
  const navigate = useNavigate()
  const toast = useToast()
  const { mutate: createRequest } = useNewSharedDiagnostic()
  const [receiverDoctorDisplayName, setReceiverDoctorDisplayName] = useState<
    string | null
  >(null)
  const [selectedPatientData, setSelectedPatientData] = useState<any>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [modalKey, setModalKey] = useState(0)
  const { t } = useTranslation()
  const prevIsOpenRef = useRef(false)

  // Determine if we're in patient context
  const isPatientContext = contextPatientId !== undefined && contextPatientId !== null

  // Define all steps, but conditionally exclude step 0 if in patient context
  const allSteps = [
    {
      title: t('sharedDiagnostic.steps.step0Title') || 'Sélection du patient',
      description: t('sharedDiagnostic.steps.step0Description') || 'Choisir le patient concerné',
    },
    {
      title: t('sharedDiagnostic.steps.step1Title'),
      description: t('sharedDiagnostic.steps.step1Description'),
    },
    {
      title: t('sharedDiagnostic.steps.step2Title'),
      description: t('sharedDiagnostic.steps.step2Description'),
    },
    {
      title: t('sharedDiagnostic.steps.step3Title'),
      description: t('sharedDiagnostic.steps.step3Description'),
    },
  ]

  // Skip patient selection step if patient context is provided
  const steps = isPatientContext ? allSteps.slice(1) : allSteps

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  })

  const methods = useForm<RequestFormData>({
    resolver: yupResolver<RequestFormData>(NewRequest),
    mode: 'onChange',
    defaultValues: {
      patientId:
        contextPatientId ||
        patientData?.id ||
        null,
      title: '',
      description: '',
      category: '',
      tagIds: [],
      files: [],
      receiverEmail: '',
      openWhiteBoard: false,
    },
  })

  // Update form when contextPatientId is provided
  useEffect(() => {
    if (contextPatientId) {
      methods.setValue('patientId', contextPatientId, { shouldValidate: true })
      if (patientData) {
        setSelectedPatientData(patientData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextPatientId, patientData])

  const handleClose = () => {
    methods.reset()
    setActiveStep(0)
    setReceiverDoctorDisplayName(null)
    setSelectedPatientData(null)
    onClose()
  }

  const handlePrevStep = () => setActiveStep(activeStep - 1)

  const handleNextStep = async () => {
    let isValid = false

    // Calculate the actual step based on whether we're in patient context
    const actualStep = isPatientContext ? activeStep + 1 : activeStep

    const ensuredPatientId =
      methods.getValues('patientId') ??
      contextPatientId ??
      patientData?.id ??
      null

    if (ensuredPatientId) {
      methods.setValue('patientId', ensuredPatientId, { shouldValidate: true })
    }

    if (actualStep === 0) {
      // Validate patient selection (only when NOT in patient context)
      const patientId = methods.getValues('patientId')
      if (!patientId) {
        toast({
          title: t('shared.errors.errorMsg'),
          description: t('sharedDiagnostic.patientSelection.required') || 'Veuillez sélectionner un patient',
          status: 'error',
          position: 'top',
          duration: 3000,
          isClosable: true,
        })
        return
      }
      isValid = true
    } else if (actualStep === 1) {
      // Validate request details
      isValid = await methods.trigger(['title', 'description', 'category', 'tagIds', 'files'])
    } else if (actualStep === 2) {
      // Validate doctor selection
      isValid = await methods.trigger(['receiverEmail'])
    }

    if (isValid) {
      setActiveStep(activeStep + 1)
    }
  }

  const onSubmit = (data: RequestFormData) => {
    console.log('🚀 NewRequestModal - onSubmit called with data:', data)
    console.log('🚀 NewRequestModal - data.patientId:', data.patientId)
    console.log('🚀 NewRequestModal - contextPatientId:', contextPatientId)
    console.log('🚀 NewRequestModal - patientData?.id:', patientData?.id)

    // Helper to check if value is valid (not null, undefined, or NaN)
    const isValidId = (val: any) => val !== null && val !== undefined && !Number.isNaN(val)

    const ensuredPatientId =
      (isValidId(data.patientId) ? data.patientId : null) ||
      (isValidId(contextPatientId) ? contextPatientId : null) ||
      (isValidId(patientData?.id) ? patientData?.id : null)

    console.log('🚀 NewRequestModal - ensuredPatientId:', ensuredPatientId)

    if (!ensuredPatientId) {
      console.error('❌ NewRequestModal - No patient ID found!')
      toast({
        title: t('shared.errors.errorMsg'),
        description: t('sharedDiagnostic.patientSelection.required') || 'Veuillez sélectionner un patient',
        status: 'error',
        position: 'top',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    createRequest(
      {
        patientId: ensuredPatientId,
        title: data.title,
        description: data.description,
        category: data.category,
        files: data.files,
        tagIds: data.tagIds,
        receiverEmail: data.receiverEmail,
      },
      {
        onSuccess: (response) => {
          if (data.openWhiteBoard) {
            navigate(
              `/patients/${data.patientId}/diagnostics/${response.data.id}`
            )
          } else {
            toast({
              title: t('shared.common.createdWithSuccess'),
              status: 'success',
              position: 'top',
              duration: 5000,
              isClosable: true,
            })
          }
          if (onRequestCreated) onRequestCreated()
          handleClose()
        },
        onError: (error) => {
          const axiosError = error as AxiosError<{ message: string }>
          const errorMessage =
            axiosError.response?.data?.message ||
            axiosError.message ||
            'An unexpected error occurred'

          toast({
            title: t('shared.errors.errorMsg'),
            description: errorMessage,
            status: 'error',
            position: 'top',
            duration: 5000,
            isClosable: true,
          })
        },
      }
    )
  }

  useEffect(() => {
    if (searchParams.get('newRequest') === 'true') {
      onOpen()
      setModalKey(prev => prev + 1)
      searchParams.delete('newRequest')
      setSearchParams(searchParams)
    }
  }, [searchParams, onOpen, setSearchParams])

  // Increment modalKey when modal opens to force remount
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      setModalKey(prev => prev + 1)
      // Reset form and states when modal opens
      methods.reset({
        patientId: contextPatientId || null,
        title: '',
        description: '',
        category: '',
        tagIds: [],
        files: [],
        receiverEmail: '',
        openWhiteBoard: false,
      })
      if (!contextPatientId) {
        setSelectedPatientData(null)
      }
      setReceiverDoctorDisplayName(null)
      setActiveStep(0)
    }
    prevIsOpenRef.current = isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contextPatientId])

  const renderStepContent = (step: number) => {
    // Calculate the actual step based on whether we're in patient context
    const actualStep = isPatientContext ? step + 1 : step

    switch (actualStep) {
      case 0:
        return <PatientSelectionStep onPatientSelect={setSelectedPatientData} />
      case 1:
        return <RequestDetailsStep />
      case 2:
        return (
          <DoctorSelectionStep
            key={modalKey}
            resetKey={modalKey}
            setReceiverDoctorDisplayName={setReceiverDoctorDisplayName}
          />
        )
      case 3:
        return <ReviewStep />
      default:
        return null
    }
  }

  return (
    <>
      <Button
        onClick={onOpen}
        leftIcon={<Plus />}
        colorScheme="purple"
        size="lg"
        fontWeight={600}
        borderRadius="xl"
        px={8}
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: 'xl',
        }}
        transition="all 0.2s"
      >
        {t('sharedDiagnostic.addRequest')}
      </Button>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={handleClose}
        size="6xl"
        isCentered
        motionPreset="slideInBottom"
      >
        <Box fontFamily={'Poppins'}>
          <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
          <ModalContent
            maxW={'70%'}
            borderRadius="2xl"
            overflow="hidden"
            boxShadow="2xl"
          >
            <ModalHeader
              bgGradient="linear(135deg, brand.purple 0%, purple.500 50%, pink.400 100%)"
              color="white"
              py={6}
              px={8}
            >
              <Box display="flex" alignItems="center" gap={3} mb={4}>
                <Icon as={LuSend} boxSize={7} />
                <Box flex={1}>
                  <Text fontSize="2xl" fontWeight={600}>
                    {t('sharedDiagnostic.addRequestTitle')}
                  </Text>
                  <HStack spacing={2} mt={1}>
                    <Text fontSize="sm" fontWeight={400} opacity={0.9}>
                      {selectedPatientData
                        ? `${selectedPatientData.firstName} ${selectedPatientData.lastName}`
                        : patientData
                          ? `${patientData.firstName} ${patientData.lastName}`
                          : t('sharedDiagnostic.patientSelection.selectPatient') || 'Sélectionner un patient'}
                      {receiverDoctorDisplayName
                        ? ` ${t('sharedDiagnostic.to')} ${receiverDoctorDisplayName}`
                        : ''}
                    </Text>
                    {isPatientContext && patientData && (
                      <Badge
                        colorScheme="green"
                        variant="solid"
                        fontSize="xs"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                      >
                        {t('sharedDiagnostic.patientSelection.selected')}
                      </Badge>
                    )}
                  </HStack>
                </Box>
              </Box>
              <ModalCloseButton
                color="white"
                size="lg"
                _hover={{ bg: 'whiteAlpha.300' }}
              />

              <Box bg="whiteAlpha.200" borderRadius="xl" p={4} mt={4}>
                <Text fontSize="sm" fontWeight={500} mb={3} opacity={0.9}>
                  {t('shared.common.step')} {activeStep + 1} / {steps.length}:{' '}
                  {steps[activeStep].description}
                </Text>
                <Stepper size="sm" index={activeStep} colorScheme="purple">
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepIndicator
                        bg={
                          index === activeStep
                            ? 'white'
                            : index < activeStep
                              ? 'green.400'
                              : 'whiteAlpha.400'
                        }
                        color={index === activeStep ? 'purple.500' : 'white'}
                        borderColor="white"
                      >
                        <StepStatus complete={<StepIcon />} />
                      </StepIndicator>
                      <StepSeparator />
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </ModalHeader>
            <ModalBody bg="gray.50" px={8} py={8}>
              <FormProvider {...methods}>
                <form
                  onSubmit={(e) => {
                    // Only allow submission via the Submit button click
                    if (activeStep !== steps.length - 1) {
                      e.preventDefault()
                    } else {
                      methods.handleSubmit(onSubmit)(e)
                    }
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      bg="white"
                      borderRadius="xl"
                      p={6}
                      minH="400px"
                      boxShadow="sm"
                    >
                      {renderStepContent(activeStep)}
                    </Box>

                    <ButtonGroup
                      w="100%"
                      justifyContent="space-between"
                      spacing={4}
                      my={6}
                    >
                      <Button
                        variant="outline"
                        border={'2px solid'}
                        borderColor={'gray.300'}
                        color="gray.600"
                        onClick={
                          activeStep === 0 ? handleClose : handlePrevStep
                        }
                        flex="1"
                        size="lg"
                        fontWeight={600}
                        _hover={{
                          bg: 'gray.100',
                          borderColor: 'gray.400',
                        }}
                      >
                        {activeStep === 0
                          ? t('shared.common.cancel')
                          : t('shared.common.previous')}
                      </Button>
                      {activeStep === steps.length - 1 ? (
                        <Button
                          colorScheme="purple"
                          type="button"
                          flex="1"
                          size="lg"
                          fontWeight={600}
                          leftIcon={<CheckIcon />}
                          onClick={methods.handleSubmit(onSubmit)}
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg',
                          }}
                          transition="all 0.2s"
                        >
                          {t('shared.common.confirm')}
                        </Button>
                      ) : (
                        <Button
                          colorScheme="purple"
                          onClick={handleNextStep}
                          flex="1"
                          size="lg"
                          fontWeight={600}
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg',
                          }}
                          transition="all 0.2s"
                        >
                          {t('shared.common.next')}
                        </Button>
                      )}
                    </ButtonGroup>
                  </Stack>
                </form>
              </FormProvider>
            </ModalBody>
          </ModalContent>
        </Box>
      </Modal>
    </>
  )
}
